import React, { useState, useEffect } from 'react';
import { Copy, RefreshCw, Server, Shield, Globe, Info, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';

export default function IntegrationSettings() {
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookToken, setWebhookToken] = useState('sk_live_51M...');
    const [externalUrl, setExternalUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [syncInterval, setSyncInterval] = useState('5');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Simulating backend generated URL
        const origin = window.location.origin.replace('5173', '5000'); // Assuming backend on 5000
        setWebhookUrl(`${origin}/api/integrations/webhook/admission`);
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleGenerateToken = () => {
        // Mock token generation
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let token = 'sk_test_';
        for (let i = 0; i < 24; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
        setWebhookToken(token);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">

            {/* Header Section */}
            <div className="bg-gradient-to-r from-purple-50 to-white dark:from-gray-800 dark:to-gray-800 p-6 rounded-xl border border-purple-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                        <Server size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Central de Integrações</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Conecte o Sistema de Gestão de Leitos com AGHU, Tasy, MV e outros prontuários eletrônicos.</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Lado Esquerdo: RECEBER DADOS (Webhook) */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200">Recepção de Dados (Webhook)</h3>
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full uppercase">Passivo</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                            Use esta opção quando o <strong>sistema externo</strong> envia os dados para nós.
                            Forneça a URL e o Token abaixo para a equipe de TI do outro hospital.
                            Assim, quando uma alta ocorrer lá, recebemos instantaneamente aqui.
                        </p>
                    </div>

                    <div className="p-6 space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Endpoint URL (Destino)</label>
                            <div className="flex gap-2">
                                <div className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-300 break-all">
                                    {webhookUrl}
                                </div>
                                <button
                                    onClick={handleCopy}
                                    className={`p-2 rounded-lg border transition-colors ${copied ? 'bg-green-50 border-green-200 text-green-600' : 'bg-white border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200'}`}
                                    title="Copiar URL"
                                >
                                    {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                <Info size={10} /> Copie este endereço e configure no webhook do sistema de origem.
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Token de Autenticação (Bear)</label>
                            <div className="flex gap-2">
                                <div className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 dark:text-white tracking-wide">
                                    {webhookToken}
                                </div>
                                <button
                                    onClick={handleGenerateToken}
                                    className="p-2 rounded-lg border bg-white border-gray-200 text-gray-500 hover:text-purple-600 hover:border-purple-200 transition-colors"
                                    title="Gerar Novo Token"
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                <Shield size={10} /> Esta chave garante que apenas fontes autorizadas enviem dados. Mantenha em segredo.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Lado Direito: BUSCAR DADOS (Polling) */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200">Busca Ativa (Polling)</h3>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full uppercase">Ativo</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                            Use esta opção quando <strong>nós precisamos ir buscar</strong> os dados.
                            Configure aqui o endereço da API do sistema externo (ex: AGHU) e a frequência de busca.
                            Nosso robô irá consultar periodicamente por novas alterações.
                        </p>
                    </div>

                    <div className="p-6 space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">URL da API Externa</label>
                            <div className="relative">
                                <Globe className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="ex: https://aghu.hospital.com.br/api/altas/v1"
                                    className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    value={externalUrl}
                                    onChange={(e) => setExternalUrl(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Chave de Acesso (API Key / Token)</label>
                            <div className="relative">
                                <Shield className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="password"
                                    placeholder="Cole a chave fornecida pelo TI externo"
                                    className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Frequência de Sincronização</label>
                            <select
                                className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                value={syncInterval}
                                onChange={(e) => setSyncInterval(e.target.value)}
                            >
                                <option value="1">A cada 1 minuto (Tempo Real)</option>
                                <option value="5">A cada 5 minutos (Recomendado)</option>
                                <option value="15">A cada 15 minutos</option>
                                <option value="60">A cada 1 hora</option>
                            </select>
                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                <Info size={10} /> Intervalos menores consomem mais recursos da rede.
                            </p>
                        </div>

                        <div className="pt-4 flex justify-end">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2">
                                <CheckCircle2 size={16} />
                                Salvar Configuração de Conexão
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}
